import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faPaperPlane, faWandMagicSparkles, faHistory, faPlus,
  faTriangleExclamation, faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { apiJson } from '../../../components/apiClient';
import AgentResultCard from './AgentResultCard';

// Painel do Agente Operacional de IA (AGENTE_OPERACIONAL_IA_CRM.md §43-§46).
// Deliberadamente NÃO é um clone de ChatGPT: cada resposta pode carregar um
// "card de resultado" estruturado (AgentResultCard) e/ou uma ação de
// confirmação (lote de WhatsApp) além do texto — não é só bolha de chat.
// Entrada global (botão "Assistente" na Topbar, ver Topbar.jsx) + contexto
// de tela: `telaContexto` vem do CrmShell (tela atual + entidade em foco,
// quando existir) e é reenviado em toda mensagem, pra tools como
// `create_project_task` conseguirem herdar `project_id` sem o usuário
// repetir ("crie uma tarefa aqui" dentro de um projeto já aberto).
//
// Uma limitação honesta: o backend responde de uma vez só (não há streaming
// de passos intermediários), então o estado de "progresso" durante a
// chamada é um indicador de "pensando/executando" único — não uma lista de
// passos em tempo real (exigiria SSE/WebSocket, que não existe nesta etapa).

const SUGESTOES_PADRAO = [
  'Quais clientes estão com pagamento em atraso?',
  'Qual o saldo em aberto de um cliente?',
  'Quais produtos estão com estoque baixo?',
];

function rotuloTelaContexto(telaContexto) {
  if (!telaContexto) return null;
  const nomes = {
    inicio: 'Início', clientes: 'Clientes', projetos: 'Projetos', financeiro: 'Financeiro',
    estoque: 'Estoque', patrimonio: 'Patrimônio', documentos: 'Documentos',
  };
  const base = nomes[telaContexto.tela] || null;
  if (!base) return null;
  const entidade = telaContexto.entity_type && telaContexto.entity_id ? ` · ${telaContexto.entity_type}` : '';
  return `${base}${entidade}`;
}

function BolhaTyping() {
  return (
    <div className="crm-agent-msg crm-agent-msg-assistant">
      <div className="crm-agent-bubble crm-agent-typing" style={{ display: 'inline-flex' }}>
        <span /><span /><span />
      </div>
    </div>
  );
}

function MensagemAgente({ msg, onConfirmarLote, onNavegar }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`crm-agent-msg ${isUser ? 'crm-agent-msg-user' : 'crm-agent-msg-assistant'} ${msg.erro ? 'crm-agent-msg-error' : ''}`}>
      {msg.text && <div className="crm-agent-bubble">{msg.text}</div>}
      {msg.data?.resultado && <AgentResultCard tool={msg.data.tool} resultado={msg.data.resultado} onNavegar={onNavegar} />}
      {msg.actions && msg.actions.map((acao) => (
        <div className="crm-agent-confirm" key={acao.bulk_job_id}>
          <div className="crm-agent-confirm-title">
            <FontAwesomeIcon icon={faTriangleExclamation} /> Confirmação necessária
          </div>
          <div>Este envio de WhatsApp em lote só acontece depois da sua confirmação — nada foi enviado ainda.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="crm-btn crm-btn-primary crm-btn-sm"
              disabled={msg.confirmando || msg.confirmado}
              onClick={() => onConfirmarLote(acao.bulk_job_id, msg.id)}
            >
              {msg.confirmado ? (<><FontAwesomeIcon icon={faCheck} /> Confirmado</>) : msg.confirmando ? 'Enviando...' : acao.label}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AgentPanel({ open, onClose, telaContexto, onNavegar }) {
  const [mensagens, setMensagens] = useState([]);
  const [input, setInput] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [erroGeral, setErroGeral] = useState(null);
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const listRef = useRef(null);
  const idRef = useRef(0);
  const proximoId = () => (idRef.current += 1);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [mensagens, enviando]);

  if (!open) return null;

  const novaConversa = () => {
    setConversationId(null);
    setMensagens([]);
    setErroGeral(null);
    setMostrarHistorico(false);
  };

  const abrirHistorico = async () => {
    setMostrarHistorico((v) => !v);
    if (historico.length > 0) return;
    setCarregandoHistorico(true);
    try {
      const result = await apiJson('/agente/conversas');
      if (result && result.status === 'ok') setHistorico(result.conversas || []);
    } catch (e) {
      // histórico é um recurso secundário — falha aqui não deve travar o painel
    } finally {
      setCarregandoHistorico(false);
    }
  };

  const carregarConversa = async (id) => {
    setMostrarHistorico(false);
    setErroGeral(null);
    try {
      const result = await apiJson(`/agente/conversas/${id}`);
      if (!result || result.status !== 'ok' || !result.conversa) {
        setErroGeral('Não consegui carregar essa conversa.');
        return;
      }
      const conversa = result.conversa;
      const carregadas = (conversa.execucoes || []).flatMap((exec) => {
        const resp = exec.structured_response || {};
        const turnos = [{ id: proximoId(), role: 'user', text: exec.mensagem_usuario }];
        turnos.push({
          id: proximoId(), role: 'assistant', text: resp.message, data: resp.data,
          actions: resp.actions, confirmado: exec.status === 'CONCLUIDA' && resp.confirmation_required,
        });
        return turnos;
      });
      setMensagens(carregadas);
      setConversationId(conversa.id);
    } catch (e) {
      setErroGeral('Não consegui carregar essa conversa.');
    }
  };

  const enviar = async (textoForcado) => {
    const texto = (textoForcado ?? input).trim();
    if (!texto || enviando) return;
    setErroGeral(null);
    setInput('');
    setMensagens((prev) => [...prev, { id: proximoId(), role: 'user', text: texto }]);
    setEnviando(true);
    try {
      const result = await apiJson('/agente/mensagem', {
        method: 'POST',
        body: JSON.stringify({ mensagem: texto, conversation_id: conversationId, tela_contexto: telaContexto || {} }),
      });
      if (result.status === 'erro') {
        setMensagens((prev) => [...prev, { id: proximoId(), role: 'assistant', text: result.mensagem || 'Não consegui processar sua mensagem.', erro: true }]);
      } else {
        setConversationId(result.conversation_id);
        setMensagens((prev) => [...prev, {
          id: proximoId(), role: 'assistant', text: result.message, data: result.data,
          actions: result.confirmation_required ? result.actions : null,
        }]);
      }
    } catch (e) {
      setMensagens((prev) => [...prev, { id: proximoId(), role: 'assistant', text: 'Não consegui falar com o Assistente agora. Tente de novo em instantes.', erro: true }]);
    } finally {
      setEnviando(false);
    }
  };

  const confirmarLote = async (bulkJobId, msgId) => {
    setMensagens((prev) => prev.map((m) => (m.id === msgId ? { ...m, confirmando: true } : m)));
    try {
      const result = await apiJson('/agente/confirmar', { method: 'POST', body: JSON.stringify({ bulk_job_id: bulkJobId }) });
      if (!result || result.status === 'erro' || !result.lote) {
        setMensagens((prev) => [...prev, { id: proximoId(), role: 'assistant', text: result?.mensagem || 'Não consegui confirmar o envio.', erro: true }]);
        setMensagens((prev) => prev.map((m) => (m.id === msgId ? { ...m, confirmando: false } : m)));
        return;
      }
      setMensagens((prev) => prev.map((m) => (m.id === msgId ? { ...m, confirmando: false, confirmado: true } : m)));
      setMensagens((prev) => [...prev, { id: proximoId(), role: 'assistant', text: `Envio confirmado — ${result.lote.total_itens} mensagem(ns) entrando na fila, com intervalo entre cada uma.` }]);
      acompanharLote(bulkJobId);
    } catch (e) {
      setMensagens((prev) => prev.map((m) => (m.id === msgId ? { ...m, confirmando: false } : m)));
      setMensagens((prev) => [...prev, { id: proximoId(), role: 'assistant', text: 'Não consegui confirmar o envio. Tente de novo.', erro: true }]);
    }
  };

  const acompanharLote = (bulkJobId) => {
    let tentativas = 0;
    const intervalo = setInterval(async () => {
      tentativas += 1;
      try {
        const result = await apiJson(`/agente/lotes/${bulkJobId}`);
        const job = result && result.status === 'ok' ? result.lote : null;
        if (job && job.status && job.status !== 'PENDENTE' && job.status !== 'PROCESSANDO') {
          clearInterval(intervalo);
          const enviados = (job.resultado || []).filter((r) => r.ok).length;
          const falhas = (job.resultado || []).length - enviados;
          setMensagens((prev) => [...prev, {
            id: proximoId(), role: 'assistant',
            text: job.status === 'CONCLUIDO'
              ? `Lote concluído: ${enviados} enviada(s)${falhas ? `, ${falhas} com falha` : ''}.`
              : `O lote terminou com status ${job.status} — verifique a conexão do WhatsApp.`,
          }]);
        }
      } catch (e) {
        // silencioso — próxima checagem tenta de novo
      }
      if (tentativas >= 20) clearInterval(intervalo); // ~40s de acompanhamento; depois o usuário pode reabrir e checar de novo
    }, 2000);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  const contextoLabel = rotuloTelaContexto(telaContexto);

  return (
    <div className="crm-overlay crm-sheet-overlay" onClick={onClose}>
      <div className="crm-sheet crm-agent-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal-header">
          <div>
            <h3 className="crm-modal-title"><FontAwesomeIcon icon={faWandMagicSparkles} /> Assistente</h3>
            {contextoLabel && <div className="crm-agent-header-sub">Contexto: {contextoLabel}</div>}
          </div>
          <div className="crm-agent-header-actions">
            <button className="crm-icon-btn" title="Nova conversa" aria-label="Nova conversa" onClick={novaConversa}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
            <button className="crm-icon-btn" title="Histórico" aria-label="Histórico" onClick={abrirHistorico}>
              <FontAwesomeIcon icon={faHistory} />
            </button>
            <button className="crm-icon-btn" onClick={onClose} aria-label="Fechar"><FontAwesomeIcon icon={faTimes} /></button>
          </div>
        </div>

        <div className="crm-agent-body">
          {mostrarHistorico ? (
            <div className="crm-agent-messages">
              {carregandoHistorico ? (
                <div className="crm-form-hint">Carregando...</div>
              ) : historico.length === 0 ? (
                <div className="crm-form-hint">Nenhuma conversa anterior ainda.</div>
              ) : (
                <div className="crm-agent-history-list">
                  {historico.map((c) => (
                    <button key={c.id} className="crm-agent-history-item" onClick={() => carregarConversa(c.id)}>
                      {c.titulo || 'Conversa sem título'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="crm-agent-messages" ref={listRef}>
              {mensagens.length === 0 && (
                <div>
                  <div className="crm-form-hint" style={{ marginBottom: 12 }}>
                    Pergunte algo, peça uma consulta ou uma ação — o Assistente usa exatamente as suas permissões no CRM.
                  </div>
                  <div className="crm-agent-suggestions">
                    {SUGESTOES_PADRAO.map((s) => (
                      <button key={s} className="crm-agent-suggestion-chip" onClick={() => enviar(s)}>{s}</button>
                    ))}
                  </div>
                </div>
              )}
              {mensagens.map((m) => (
                <MensagemAgente
                  key={m.id}
                  msg={m}
                  onConfirmarLote={confirmarLote}
                  onNavegar={onNavegar ? (destino, contexto) => { onNavegar(destino, contexto); onClose(); } : undefined}
                />
              ))}
              {enviando && <BolhaTyping />}
              {erroGeral && <div className="crm-agent-msg crm-agent-msg-error"><div className="crm-agent-bubble">{erroGeral}</div></div>}
            </div>
          )}

          <div className="crm-agent-input-row">
            <textarea
              className="crm-agent-textarea"
              rows={1}
              placeholder="Escreva para o Assistente..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={enviando}
            />
            <button className="crm-btn crm-btn-primary crm-btn-sm" onClick={() => enviar()} disabled={enviando || !input.trim()} aria-label="Enviar">
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AgentPanel;
